/**
 * Wire contract for the Birdwatch realtime relay (the `realtime` workspace —
 * @birdwatch/relay, Redis Streams over SSE). This is the ONE home for the
 * channel/event schema: the relay's SSE route, the publishers, and the
 * webapp's subscriber all import it, so the deployables cannot drift.
 *
 * Channels are rooms keyed by a 24-char `[0-9a-z]` nanoid entity id: media
 * events use the `attachmentId`; quote and subscriber events use the `orderId`.
 * Public channels (`public:<id>`, below) are the one code-level exception: a
 * stream of only public channels may open without a token. A `user:<User.id>`
 * channel is addressed to one person BY CONVENTION ONLY: the relay checks its
 * shape, not its owner, so any signed-in user who knows an id may subscribe to
 * one — which is why its events obey the ids-only rule like every other channel.
 * Subscribing requires a valid Clerk session token of that environment; identity
 * is checked when the stream opens (re-checked on reconnect, at most every ~5 min),
 * not per event. Any signed-in user may subscribe to any well-shaped channel id;
 * access is not per order/attachment yet, so payloads stay ids-only. Only Clerk
 * session tokens are accepted (no actor / API-key tokens).
 */

import * as z from "zod";
import { isNanoid } from "./nanoid";

/**
 * Every change a quote announces on its order's channel: the activity-feed verbs
 * (platform `ORDER_QUOTE_ACTIVITY_TYPES` must stay a subset — enforced there with
 * `satisfies`) plus four broadcast-only verbs that write no activity row:
 * DELETE_QUOTE (hard-deleted draft), VIEW_QUOTE (first customer view),
 * EDIT_QUOTE_ITEMS (item write on the quote's scope), EXPIRE_QUOTE (hourly sweep).
 * TRANSITION_SCOPE_TO_QUOTE: an order's own scope was adopted as a new quote.
 */
export const QUOTE_BROADCAST_ACTIVITY_TYPES = [
  "CREATE_QUOTE",
  "RENAME_QUOTE",
  "DESCRIBE_QUOTE",
  "ISSUE_QUOTE",
  "UPDATE_QUOTE",
  "DISCARD_QUOTE_EDITS",
  "RESEND_QUOTE",
  "REOPEN_QUOTE",
  "ACCEPT_QUOTE",
  "DECLINE_QUOTE",
  "CANCEL_QUOTE",
  "DUPLICATE_QUOTE",
  "TRANSITION_SCOPE_TO_QUOTE",
  "DELETE_QUOTE",
  "VIEW_QUOTE",
  "EDIT_QUOTE_ITEMS",
  "EXPIRE_QUOTE",
] as const;

/**
 * `quote.changed` — channel = the quote's orderId. Ids only (strict: an extra key
 * fails the emit-time parse) because any signed-in user may subscribe to any order
 * channel (no per-order access check yet). `audience: "TEAM"` events are office-only; the customer client
 * ignores them — they are still DELIVERED to any subscriber of the order channel.
 * Clients refetch through the API, which enforces visibility.
 */
export const quoteChangedEventSchema = z.strictObject({
  orderId: z.string(),
  quoteId: z.string(),
  activityType: z.enum(QUOTE_BROADCAST_ACTIVITY_TYPES),
  audience: z.enum(["TEAM", "ALL"]),
});
export type QuoteChangedEvent = z.infer<typeof quoteChangedEventSchema>;
export type QuoteBroadcastActivityType = QuoteChangedEvent["activityType"];

/**
 * `subscribers.changed` — channel = the orderId. Deliberately a bare ping: the
 * only field is the channel's own id, so the event says "this order's subscriber
 * list moved" and nothing about WHO moved.
 *
 * That omission is the design, not an oversight. The relay checks only that the
 * subscriber is signed in, not that they may see the order, so any signed-in user
 * holding an order id could otherwise read the set of user ids following it, and
 * watch it change — an identity leak the quote payload's ids-only rule does not cover. Subscribers refetch through the
 * authenticated order-detail endpoint, which enforces visibility, and treat the
 * event as an idempotent hint (history replays on reconnect).
 */
export const subscribersChangedEventSchema = z.strictObject({
  orderId: z.string(),
});
export type SubscribersChangedEvent = z.infer<
  typeof subscribersChangedEventSchema
>;

/**
 * `visit.changed` — channel = the visit's orderId. A visit on the order was
 * proposed, answered, booked, started, completed, cancelled, expired, deleted or
 * otherwise moved. Ids only (strict): the visit's status, technicians and actor
 * stay off the wire because any signed-in user may subscribe to any order
 * channel. Clients refetch the order's visits through the API, which enforces
 * visibility.
 */
export const visitChangedEventSchema = z.strictObject({
  orderId: z.string(),
  visitId: z.string(),
});
export type VisitChangedEvent = z.infer<typeof visitChangedEventSchema>;

/**
 * `notification.received` — channel = `user:<recipientId>`. **Ids and state only.**
 *
 * The webapp fetches `GET /notifications/:id` before it toasts — one authenticated
 * request, no polling. Three reasons the row's text is NOT on the wire: the channel
 * is not access-checked, so anyone who knows a `User.id` may read what rides it;
 * `title` and `subtitle` are not columns (they are rendered on read from
 * batch-loaded entities); and the relay keeps an hour of history in the shared
 * Redis keyspace, which is where user-authored text is already treated as PII.
 */
export const notificationReceivedEventSchema = z.strictObject({
  id: z.string(),
  type: z.string(),
  category: z.string(),
  subjectType: z.string(),
  subjectId: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  readAt: z.string().nullable(),
  createdAt: z.string(),
  /**
   * The emit instant. The toast's freshness check reads THIS, never `createdAt`,
   * so a backdated or re-announced row is judged on when it was sent.
   */
  announcedAt: z.string(),
});
export type NotificationReceivedEvent = z.infer<
  typeof notificationReceivedEventSchema
>;

/**
 * `notification.counts` — channel = `user:<recipientId>`. The badge value,
 * recomputed server-side through the same inbox predicate the list uses.
 */
export const notificationCountsEventSchema = z.strictObject({
  unread: z.number().int().min(0),
  /**
   * The compute instant. Two writes race and the relay replays history on
   * reconnect; the client keeps the newest `at` and drops the rest.
   */
  at: z.string(),
});
export type NotificationCountsEvent = z.infer<
  typeof notificationCountsEventSchema
>;

/**
 * `inbox.activity` — channels = {@link realtimeInboxChannels}: the team-wide
 * {@link REALTIME_INBOX_CHANNEL} AND the conversation's home (its `placeId`). A
 * conversation in the inbox projection (an order thread or a home support
 * conversation) changed: it got a new message, a read wrote receipts (which can flip the
 * sent/read mark on the author's row), or — on the team channel only — a staff member
 * read it, clearing the office's shared unread badge that every other manager's inbox
 * renders.
 *
 * The home channel is how a household hears it: a member's Chats shows one home, so it
 * subscribes to that one id — never to the team channel, which carries every message
 * in the company and would wake every household on each one.
 *
 * **Only the opaque `Channel.id`.** Office staff see every conversation, so their
 * inbox cannot subscribe per conversation — a thread whose first message just arrived
 * is not on their page yet. One team-wide channel therefore carries every inbox's
 * activity; the relay admits only the team to it, but the home channel beside it
 * is shape-checked only. So it carries no cid: a cid embeds the order or place id,
 * which is a subscribe capability for that entity's own channel. `Channel.id` is not a
 * relay channel, and reading the conversation still goes through the access-checked
 * inbox API. Clients refetch; the id only lets a surface with that thread open refresh
 * it too.
 */
export const inboxActivityEventSchema = z.strictObject({
  channelId: z.string(),
});
export type InboxActivityEvent = z.infer<typeof inboxActivityEventSchema>;

// ============================================================================
// `<entity>.changed` — the one convention for "this record changed; refetch it"
// ============================================================================
//
// Every write the platform accepts announces itself with one of these (route
// `publish`, or `emitRealtime` from the job that wrote). Rules shared by all:
//
// - **Ids only, strict.** The relay checks who a subscriber is, not what they may
//   see, so a payload names WHICH record moved and never what it now says.
//   Subscribers refetch through the API, which enforces visibility.
// - **`changed` covers create, update and delete.** A subscriber refetches the
//   lists and details it shows either way; nothing branches on the verb.
// - **Nullable parent ids** let one event serve every screen that shows the
//   record: an order's task and a home's task are the same `task.changed`.
// - The channels each event rides are listed in
//   docs/realtime/event-catalog.md (team channel, the parent
//   order / home / account, the user).

/** `order.changed` — the order record itself (status, fields, vendors, flags, links, attachments, feedback, draft/publish). */
export const orderChangedEventSchema = z.strictObject({ orderId: z.string() });

/** `task.changed` — a task was created, edited, completed or deleted. */
export const taskChangedEventSchema = z.strictObject({
  taskId: z.string(),
  orderId: z.string().nullable(),
  placeId: z.string().nullable(),
});

/** `comment.changed` — an internal (team-only) note on an order. The note's text stays off the wire. */
export const commentChangedEventSchema = z.strictObject({
  orderId: z.string(),
  commentId: z.string(),
});

/** `message.changed` — a message in one conversation was posted, edited, deleted, pinned or read. `channelId` is the opaque `Channel.id` (never a cid). */
export const messageChangedEventSchema = z.strictObject({
  channelId: z.string(),
  messageId: z.string().nullable(),
});

/**
 * `channel.changed` — a conversation's own state changed: it was archived (by its order
 * closing, or by hand) or unarchived. `channelId` is the opaque `Channel.id` (never a
 * cid). Rides the Office Inbox, the conversation's home and the entity it hangs off, so
 * every list and open thread that shows it refetches.
 */
export const channelChangedEventSchema = z.strictObject({
  channelId: z.string(),
});

/** `typing.changed` — someone started or stopped typing in a conversation. */
export const typingChangedEventSchema = z.strictObject({
  channelId: z.string(),
  userId: z.string(),
  typing: z.boolean(),
});

/** `scope.changed` — an order's scope of work: line items, item groups, the scope document, order plans. */
export const scopeChangedEventSchema = z.strictObject({ orderId: z.string() });

/** `expense.changed` */
export const expenseChangedEventSchema = z.strictObject({
  expenseId: z.string(),
  orderId: z.string().nullable(),
});

/** `timeEntry.changed` */
export const timeEntryChangedEventSchema = z.strictObject({
  timeEntryId: z.string(),
  orderId: z.string().nullable(),
});

/** `revenue.changed` */
export const revenueChangedEventSchema = z.strictObject({
  revenueId: z.string(),
  orderId: z.string().nullable(),
});

/** `revenueMonth.changed` — a month of Birdwatch revenue was closed or reopened. Channel = the team channel. */
export const revenueMonthChangedEventSchema = z.strictObject({
  /** "2026-10" */
  month: z.string(),
});

/** Every AI-derived panel an order page renders and a job refills in the background. */
export const ORDER_INSIGHT_KINDS = [
  "SUMMARY",
  "BIRDIE_TIPS",
  "TONE",
  "VISIT_INSTRUCTION",
  "CONVERSATION_SUMMARY",
  "RECAP",
] as const;

/** `orderInsight.changed` — an AI panel of the order was regenerated. */
export const orderInsightChangedEventSchema = z.strictObject({
  orderId: z.string(),
  kind: z.enum(ORDER_INSIGHT_KINDS),
});

/** `homecareProposal.changed` */
export const homecareProposalChangedEventSchema = z.strictObject({
  proposalId: z.string(),
  orderId: z.string().nullable(),
});

/**
 * `homecarePlanOrders.changed` — a HomeCare plan order started splitting into
 * its future orders (`STARTED`, so every open plan page stops offering it),
 * finished (`CREATED`), or the job doing it gave up (`FAILED`). Channel = the
 * plan order. The outcome is what the open plan page tells its user; the
 * orders themselves are read back through the API.
 */
export const homecarePlanOrdersChangedEventSchema = z.strictObject({
  orderId: z.string(),
  outcome: z.enum(["STARTED", "CREATED", "FAILED"]),
});

/**
 * `smartAction.changed` — a Smart Action was created, re-read, moved through its
 * workflow (contact, snooze, resolve, dismiss, reopen, reassign, a step edit) or
 * deleted. Office-only screens (the boards and the order card) refetch through
 * the API, which enforces the role check.
 */
export const smartActionChangedEventSchema = z.strictObject({
  smartActionId: z.string(),
  orderId: z.string(),
});

/** `orderRequest.changed` — a member's request (cart) or one of its items. */
export const orderRequestChangedEventSchema = z.strictObject({
  orderRequestId: z.string(),
  placeId: z.string().nullable(),
});

/**
 * `place.changed` — the home record itself (address, details, manager), and its
 * files: the files of its orders, which the place detail's attachments include.
 */
export const placeChangedEventSchema = z.strictObject({ placeId: z.string() });

/** `household.changed` — who belongs to a home: members, invitations, billing grants. */
export const householdChangedEventSchema = z.strictObject({
  placeId: z.string(),
});

/** `space.changed` — a room/area of a home. */
export const spaceChangedEventSchema = z.strictObject({
  placeId: z.string(),
  spaceId: z.string().nullable(),
});

/** `listItem.changed` — a home's to-do / list items (created, edited, sorted, linked, shared, done). */
export const listItemChangedEventSchema = z.strictObject({
  placeId: z.string().nullable(),
  listItemId: z.string().nullable(),
});

/** `note.changed` — the note document of an order or a home. Channel = `notableId`. */
export const noteChangedEventSchema = z.strictObject({
  notableType: z.string(),
  notableId: z.string(),
});

/** `onboarding.changed` — a member onboarding moved a step, started, completed or reset. */
export const onboardingChangedEventSchema = z.strictObject({
  onboardingId: z.string().nullable(),
  placeId: z.string().nullable(),
  userId: z.string().nullable(),
});

/** `account.changed` — the billing account record (customer details, trackings, deletion request). */
export const accountChangedEventSchema = z.strictObject({
  accountId: z.string(),
});

/** `billing.changed` — an account's payment methods or default method. */
export const billingChangedEventSchema = z.strictObject({
  accountId: z.string(),
});

/** `subscription.changed` */
export const subscriptionChangedEventSchema = z.strictObject({
  subscriptionId: z.string(),
  accountId: z.string().nullable(),
});

/** `membership.changed` */
export const membershipChangedEventSchema = z.strictObject({
  membershipId: z.string(),
  accountId: z.string().nullable(),
});

/** `wallet.changed` — an account's wallet balance or ledger. */
export const walletChangedEventSchema = z.strictObject({
  accountId: z.string(),
});

/**
 * `deposit.changed` — channel = the orderId (and the team channel). A quote's
 * deposit moved: requested, paid, processing, failed, confirmed, waived,
 * refunded or kept. Ids only; clients refetch through the API.
 */
export const depositChangedEventSchema = z.strictObject({
  orderId: z.string(),
  orderQuoteId: z.string(),
});

/**
 * `invoice.changed` — channels: the team, the invoice's order, and its bill-to
 * account once issued. A Birdwatch invoice was drafted, edited, issued, voided,
 * paid, or its Stripe reference invoice changed. Ids only; clients refetch.
 */
export const invoiceChangedEventSchema = z.strictObject({
  invoiceId: z.string(),
  orderId: z.string().nullable(),
  accountId: z.string().nullable(),
});

/** `purchaseIntent.changed` */
export const purchaseIntentChangedEventSchema = z.strictObject({
  purchaseIntentId: z.string(),
});

/** `checkoutLink.changed` */
export const checkoutLinkChangedEventSchema = z.strictObject({
  checkoutLinkId: z.string(),
});

/** `user.changed` — a user's profile, roles, permissions, accounts, homes or activation. */
export const userChangedEventSchema = z.strictObject({ userId: z.string() });

/**
 * `userPresence.changed` — a webapp user's first relay stream opened or their last
 * one closed. Channels = `user:<watcherId>` of every user who read this user's
 * presence lately ({@link realtimePresenceWatchersKey}), so it reaches exactly the
 * screens showing their dot. Ids only, like every event: it says WHOSE presence
 * moved, not which way; subscribers refetch `GET /users/:userId/online`, which
 * also folds in the mobile websocket.
 */
export const userPresenceChangedEventSchema = z.strictObject({
  userId: z.string(),
});

/** `vendor.changed` */
export const vendorChangedEventSchema = z.strictObject({
  /** The vendor's `Account.id` — the `:vendorId` the vendor routes key on — never `Vendor.id`. */
  vendorId: z.string(),
});

/** `vendorOnboarding.changed` */
export const vendorOnboardingChangedEventSchema = z.strictObject({
  vendorOnboardingId: z.string(),
});

/** `scheduleEvent.changed` — a calendar event that is not a visit. */
export const scheduleEventChangedEventSchema = z.strictObject({
  scheduleEventId: z.string(),
});

/** `calendar.changed` — a user's Google calendar was connected, disconnected or finished a sync. */
export const calendarChangedEventSchema = z.strictObject({
  userId: z.string(),
});

/** `meet.changed` — a meet link was created or removed, or its room's participants changed. */
export const meetChangedEventSchema = z.strictObject({
  meetLinkId: z.string(),
});

/** `role.changed` — a role or its permissions (admin authorization screens). */
export const roleChangedEventSchema = z.strictObject({ roleKey: z.string() });

/** `tag.changed` */
export const tagChangedEventSchema = z.strictObject({ tagId: z.string() });

/**
 * `campaign.changed` — a member campaign: an admin's add, edit or delete (the team channel and
 * {@link REALTIME_CAMPAIGN_CHANNEL}), or one member's own history of it, a dismissal or a modal
 * shown (that member's `user:` channel).
 */
export const campaignChangedEventSchema = z.strictObject({
  campaignId: z.string(),
});

/** `location.changed` — a service location. */
export const locationChangedEventSchema = z.strictObject({
  locationId: z.string(),
});

/** `zipcode.changed` — a service-area zipcode. */
export const zipcodeChangedEventSchema = z.strictObject({
  zipcode: z.string(),
});

/** `view.changed` — a user's saved list views (default view). */
export const viewChangedEventSchema = z.strictObject({ userId: z.string() });

/**
 * `readState.changed` — a user's own read state moved across many conversations of one
 * type at once (the Field Chats' "Mark all as read"): their lists, badges and threads of
 * that type refetch. Channel = `user:<userId>` only — one event however many conversations
 * the read covered, in place of a `message.changed` per conversation that would flood the
 * channel's replay history.
 */
export const readStateChangedEventSchema = z.strictObject({
  userId: z.string(),
  /**
   * The conversations' type — today only the team's internal notes (`order_comment`, the
   * Field Chats). Widen it when another type gets a bulk read; a subscriber switches on it.
   */
  channelTypeId: z.enum(["order_comment"]),
});

/**
 * `app.released` — a new build of an app finished deploying. Channel =
 * {@link REALTIME_APP_RELEASE_CHANNEL} (public, so a signed-out page hears it too).
 * Open tabs offer a reload when `app` and `environment` are theirs.
 */
export const appReleasedEventSchema = z.strictObject({
  app: z.string(),
  environment: z.string(),
});

export const realtimeEventSchema = {
  /** Terminal processing outcome for an upload. Channel = attachmentId. The
   * webapp SUBSCRIBES to this instead of polling `/uploads/status`. A signed-out
   * upload (`viaChannel = PUBLIC`) is also announced on `public:<attachmentId>`,
   * with `aiAnalyzed: false` and nothing beyond these fields. */
  attachment: {
    processed: z.object({
      attachmentId: z.string(),
      status: z.enum(["UPLOADED", "FAILED"]),
      contentUri: z.string().nullish(),
      thumbnailUri: z.string().nullish(),
      contentType: z.string().nullish(),
      fileSize: z.number().nullish(),
      aiAnalyzed: z.boolean().nullish(),
    }),
  },
  /** Audio → media-v3 → Gemini transcription, delivered live. Channel = attachmentId, never a public channel. */
  transcript: {
    ready: z.object({
      attachmentId: z.string(),
      text: z.string(),
      model: z.string().nullish(),
    }),
    failed: z.object({
      attachmentId: z.string(),
      reason: z.string(),
    }),
  },
  /** A quote on the order changed. Channel = orderId. Subscribers invalidate and refetch. */
  quote: {
    changed: quoteChangedEventSchema,
  },
  /**
   * The order's notification-subscriber list changed — the header bell, the
   * picker's replace-set, or any rule that auto-subscribes someone. Channel =
   * orderId. Subscribers invalidate the order detail and refetch.
   */
  subscribers: {
    changed: subscribersChangedEventSchema,
  },
  /** A visit on the order changed. Channel = orderId. Subscribers refetch the order's visits. */
  visit: {
    changed: visitChangedEventSchema,
  },
  /**
   * One recipient's inbox. Channel = `user:<recipientId>` — never an order or
   * attachment channel, because these are personal.
   */
  notification: {
    received: notificationReceivedEventSchema,
    counts: notificationCountsEventSchema,
  },
  /**
   * An inbox conversation changed — a new message (channels = {@link
   * realtimeInboxChannels}: the team-wide inbox channel and the conversation's home), a
   * read that wrote receipts (same channels), or a staff read of the shared office badge
   * (team channel only). Subscribers refetch
   * their inbox list.
   */
  inbox: {
    activity: inboxActivityEventSchema,
  },
  order: { changed: orderChangedEventSchema },
  task: { changed: taskChangedEventSchema },
  comment: { changed: commentChangedEventSchema },
  channel: { changed: channelChangedEventSchema },
  message: { changed: messageChangedEventSchema },
  typing: { changed: typingChangedEventSchema },
  scope: { changed: scopeChangedEventSchema },
  expense: { changed: expenseChangedEventSchema },
  timeEntry: { changed: timeEntryChangedEventSchema },
  revenue: { changed: revenueChangedEventSchema },
  revenueMonth: { changed: revenueMonthChangedEventSchema },
  orderInsight: { changed: orderInsightChangedEventSchema },
  homecareProposal: { changed: homecareProposalChangedEventSchema },
  homecarePlanOrders: { changed: homecarePlanOrdersChangedEventSchema },
  smartAction: { changed: smartActionChangedEventSchema },
  orderRequest: { changed: orderRequestChangedEventSchema },
  place: { changed: placeChangedEventSchema },
  household: { changed: householdChangedEventSchema },
  space: { changed: spaceChangedEventSchema },
  listItem: { changed: listItemChangedEventSchema },
  note: { changed: noteChangedEventSchema },
  onboarding: { changed: onboardingChangedEventSchema },
  account: { changed: accountChangedEventSchema },
  billing: { changed: billingChangedEventSchema },
  subscription: { changed: subscriptionChangedEventSchema },
  membership: { changed: membershipChangedEventSchema },
  wallet: { changed: walletChangedEventSchema },
  deposit: { changed: depositChangedEventSchema },
  invoice: { changed: invoiceChangedEventSchema },
  purchaseIntent: { changed: purchaseIntentChangedEventSchema },
  checkoutLink: { changed: checkoutLinkChangedEventSchema },
  user: { changed: userChangedEventSchema },
  userPresence: { changed: userPresenceChangedEventSchema },
  vendor: { changed: vendorChangedEventSchema },
  vendorOnboarding: { changed: vendorOnboardingChangedEventSchema },
  scheduleEvent: { changed: scheduleEventChangedEventSchema },
  calendar: { changed: calendarChangedEventSchema },
  meet: { changed: meetChangedEventSchema },
  role: { changed: roleChangedEventSchema },
  tag: { changed: tagChangedEventSchema },
  campaign: { changed: campaignChangedEventSchema },
  location: { changed: locationChangedEventSchema },
  zipcode: { changed: zipcodeChangedEventSchema },
  view: { changed: viewChangedEventSchema },
  readState: { changed: readStateChangedEventSchema },
  app: { released: appReleasedEventSchema },
};

/** The schema map's type — the relay's `RelaySchema` for this contract. */
export type RealtimeEvents = typeof realtimeEventSchema;

/** Every event name, `family.verb` (`"order.changed"`). */
export type RealtimeEventName = {
  [F in keyof RealtimeEvents &
    string]: `${F}.${keyof RealtimeEvents[F] & string}`;
}[keyof RealtimeEvents & string];

/** The payload of one event. */
export type RealtimeEventData<E extends RealtimeEventName> =
  E extends `${infer F}.${infer N}`
    ? F extends keyof RealtimeEvents
      ? N extends keyof RealtimeEvents[F]
        ? RealtimeEvents[F][N] extends z.ZodType
          ? z.infer<RealtimeEvents[F][N]>
          : never
        : never
      : never
    : never;

// ============================================================================
// Origin — which browser tab caused an event
// ============================================================================

/**
 * Request header `@birdwatch/client` sends on every call: the tab's random
 * origin id. A route's `publish` stamps it on the events it sends, so the tab
 * that made the write recognises the echo and does not refetch what its own
 * `onSuccess` already refreshed. Events from jobs carry no origin.
 */
export const REALTIME_ORIGIN_HEADER = "X-Realtime-Origin";

/** An origin is opaque: 8–64 url-safe characters. Anything else is dropped, not forwarded. */
export function isRealtimeOrigin(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]{8,64}$/.test(value);
}

/**
 * Retention for emitted events — identical for EVERY publisher into the shared
 * keyspace: each emit trims the channel stream to `maxLength` entries and
 * refreshes an `expireAfterSecs` TTL, so transcript history (user speech — PII)
 * stays bounded and short-lived instead of accumulating one permanent Redis key
 * per attachment. An hour far exceeds the SSE reconnect/replay window.
 */
export const realtimeHistoryConfig = { maxLength: 100, expireAfterSecs: 3600 };

/**
 * The team-wide inbox channel — one fixed entity-shaped name (24 chars of `[0-9a-z]`)
 * that every Office Inbox subscribes to.
 *
 * **Staff-only** ({@link REALTIME_STAFF_CHANNELS}): the relay asks the platform before
 * it opens a stream that names it, and refuses anyone without a
 * {@link REALTIME_STAFF_ROLES} role. The payload stays an opaque `Channel.id` anyway.
 */
export const REALTIME_INBOX_CHANNEL = "officeinbox0000000000000";

/**
 * The team-wide channel every office and admin tab subscribes to: the
 * list-level side of every `<entity>.changed` (orders list, tasks, schedule,
 * vendors, accounts, …). Staff-only like the inbox channel; payloads stay ids.
 */
export const REALTIME_TEAM_CHANNEL = "officeteam00000000000000";

/**
 * Channels only the team (ADMIN/MANAGER/COORDINATOR/TECHNICIAN/EVALUATOR) may subscribe to. The one list the relay (which checks
 * a request against it), the platform (which decides) and the webapp (which must not
 * ask for one it would be refused — a 403 drops the page's whole shared stream) read.
 */
export const REALTIME_STAFF_CHANNELS: readonly string[] = [
  REALTIME_INBOX_CHANNEL,
  REALTIME_TEAM_CHANNEL,
];

/**
 * The channel every member app subscribes to for `campaign.changed`: an admin's add, edit or
 * delete changes what `GET /members/campaigns` answers for every member at once, so it cannot
 * ride the staff-only team channel or each member's `user:` channel. **Not staff-only** — any
 * signed-in user may subscribe; the payload stays the campaign id.
 */
export const REALTIME_CAMPAIGN_CHANNEL = "campaigns000000000000000";

/**
 * The public channel `app.released` rides. Public so a signed-out tab (sign-in,
 * presale, public quote pages) hears a deploy too; the payload is two labels.
 */
export const REALTIME_APP_RELEASE_CHANNEL = "public:appreleases0000000000000";

/**
 * Redis sorted set of one user's open signed-in relay streams: member = a
 * per-stream connection id, score = the epoch ms that entry stops counting. The
 * relay adds an entry when a stream opens, pushes its score forward every minute
 * while the stream stays open and, when the stream closes, cuts it to the end of
 * {@link REALTIME_PRESENCE_OFFLINE_GRACE_MS}; the platform reads it for
 * `GET /users/:userId/online` (online = at least one score still in the future).
 * Shared here so the writer and the reader cannot disagree on the key.
 *
 * The `conn` segment keeps it apart from the string key an older relay wrote under
 * `relay:presence:<userId>` — a sorted-set read of that key would fail.
 */
export const realtimePresenceKey = (userId: string): string =>
  `relay:presence:conn:${userId}`;

/**
 * How long one connection entry counts without a refresh. The relay refreshes it
 * every 60 s; two and a half refreshes' worth survives one failed or late one. It
 * also decides how long a stream that died without a close (a crashed function, a
 * dropped network) still reads as online.
 */
export const REALTIME_PRESENCE_ENTRY_TTL_MS = 150_000;

/**
 * How long a closed stream still counts, and how long the relay waits before
 * deciding its user went offline. A tab also closes its stream when it switches
 * channels (every navigation that changes them) and reopens one at once; the
 * reopened stream lands inside this window, so routine reconnects announce
 * neither online nor offline.
 */
export const REALTIME_PRESENCE_OFFLINE_GRACE_MS = 5_000;

/**
 * Redis sorted set of the users who read `userId`'s presence lately: member = the
 * reader's `User.id`, score = the epoch ms that registration stops counting. The
 * platform's `GET /users/:userId/online` registers its caller (the same
 * read-registers-the-reader contract as the legacy websocket's topic mapping), and
 * the relay announces `userPresence.changed` on the `user:` channel of each live
 * reader — so only screens that show the dot hear it, staff and members alike.
 */
export const realtimePresenceWatchersKey = (userId: string): string =>
  `relay:presence:watchers:${userId}`;

/**
 * How long one read keeps its reader registered. A mounted presence dot re-reads
 * every 60 s; two and a half reads' worth survives one late poll. A tab that stops
 * reading (dot unmounted, tab hidden) drops out on its own.
 */
export const REALTIME_PRESENCE_WATCH_TTL_MS = 150_000;

/** Whether a channel is staff-only; see {@link REALTIME_STAFF_CHANNELS}. */
export function isRealtimeStaffChannel(channel: string): boolean {
  return REALTIME_STAFF_CHANNELS.includes(channel);
}

/**
 * The roles that make up "the team", and so may subscribe to staff-only channels:
 * everyone who works in the Office Inbox — admins, managers, coordinators (who
 * hold a manager's permissions), technicians and evaluators (who hold a
 * technician's permissions).
 *
 * An allow-list: an unrecognized role is not staff, and a homeowner never is. The
 * pings carry only opaque channel ids; what a subscriber may then READ is still the
 * inbox API's call (a technician's inbox, for one, lists no support conversations).
 *
 * Platform's `CHANNEL_TEAM_ROLES` is this same list, so "who is the team" has one
 * home. It is NOT the support-staff list (`CHANNEL_SUPPORT_ROLES`, ADMIN/MANAGER/COORDINATOR),
 * which decides who may read a household's support conversation — keep the two apart.
 */
export const REALTIME_STAFF_ROLES: readonly string[] = [
  "ADMIN",
  "MANAGER",
  "COORDINATOR",
  "TECHNICIAN",
  "EVALUATOR",
];

/**
 * Where the relay asks the platform about staff-only channels, relative to the
 * platform's base URL. The relay forwards the subscriber's own bearer token, with
 * body `{ channels }`; the answer is `{ data: RealtimeAuthorizeResult }`.
 */
export const REALTIME_AUTHORIZE_PATH = "/api/v5/realtime/authorize";

/**
 * The platform's answer: the requested channels this caller may not subscribe to.
 * Empty means the stream may open. One schema for both sides — the platform returns
 * it, the relay parses it — so neither can change the shape alone.
 */
export const realtimeAuthorizeResultSchema = z.object({
  denied: z.array(z.string()),
});
export type RealtimeAuthorizeResult = z.infer<
  typeof realtimeAuthorizeResultSchema
>;

/** Whether a user with these roles may subscribe to staff-only channels. */
export function canSubscribeToStaffChannels(
  roles: readonly string[] | null | undefined,
): boolean {
  return (roles ?? []).some((role) => REALTIME_STAFF_ROLES.includes(role));
}

/**
 * Every channel a conversation's `inbox.activity` rides: the team-wide inbox channel
 * (the Office Inbox) and the conversation's home (a household's Chats, which shows one
 * home). A home id the relay would refuse is dropped rather than emitted — the team
 * channel still carries the event.
 *
 * @param placeId - The conversation's home: a support conversation's own place, an
 *   order thread's order's place; null when it could not be resolved.
 * @returns The channels to emit on, team channel first.
 */
export function realtimeInboxChannels(placeId: string | null): string[] {
  return placeId && isRealtimeChannelAllowed(placeId)
    ? [REALTIME_INBOX_CHANNEL, placeId]
    : [REALTIME_INBOX_CHANNEL];
}

/** Prefix that marks a channel anyone may subscribe to without a token. A publisher opts a stream in by emitting on `realtimePublicChannel(id)`. */
export const REALTIME_PUBLIC_CHANNEL_PREFIX = "public:";
export const realtimePublicChannel = (id: string): string =>
  `${REALTIME_PUBLIC_CHANNEL_PREFIX}${id}`;
export function isRealtimePublicChannel(channel: string): boolean {
  return (
    channel.startsWith(REALTIME_PUBLIC_CHANNEL_PREFIX) &&
    isNanoid(channel.slice(REALTIME_PUBLIC_CHANNEL_PREFIX.length))
  );
}

/**
 * Per-user channel — the addressee is in the name.
 *
 * **Not access-controlled** (deliberate, 2026-09-22): the relay checks the shape
 * and stops there, so any signed-in user who knows a `User.id` may subscribe to
 * somebody else's. The events riding it therefore stay ids-and-state, exactly
 * like every other channel's — never rendered text.
 */
export const REALTIME_USER_CHANNEL_PREFIX = "user:";
export const realtimeUserChannel = (userId: string): string =>
  `${REALTIME_USER_CHANNEL_PREFIX}${userId}`;

/**
 * The addressee's id, or null when the channel is not a user channel.
 *
 * A **shape parser**, not a check: the id it returns is never compared against
 * anybody. It exists so `isRealtimeChannelAllowed` can recognise the shape
 * without restating the prefix and the charset.
 *
 * @param channel - The channel name as the client asked for it
 * @returns The `User.id` the channel is addressed to, or null
 */
export function realtimeUserChannelOwner(channel: string): string | null {
  const match = /^user:([A-Za-z0-9_-]{1,64})$/.exec(channel);
  return match?.[1] ?? null;
}

/**
 * The only channel shapes the relay serves: a private 24-char nanoid entity id
 * (media attachmentId or orderId, charset [0-9a-z]), a public channel, or a
 * `user:<User.id>` channel. The relay rejects anything else so arbitrary Redis
 * keys in the shared keyspace can never be read as "channels".
 *
 * Passing this check is **not** an access grant for ANY of the three: the relay
 * has no per-subscriber rule, so a signed-in user may subscribe to any
 * well-shaped id — an entity's, or another person's. Every payload therefore
 * stays ids-only.
 */
export function isRealtimeChannelAllowed(channel: string): boolean {
  return (
    isNanoid(channel) ||
    isRealtimePublicChannel(channel) ||
    realtimeUserChannelOwner(channel) !== null
  );
}
